import { parseEmoji } from '@/helper/utils';
import { isFullProject } from '@/typeGuards/project.guard';
import { Project, ProjectPreview } from '@/types/project.types';
import { Shrink } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ProjectInfoCardProps {
  project: Project | ProjectPreview;
  isExpanded?: boolean;

  showEdit?: boolean;
  showFile?: boolean;
  showExpand?: boolean;
  showCollapse?: boolean;

  enableEdit?: boolean;
  enableFile?: boolean;
  enableExpand?: boolean;
  enableCollapse?: boolean;

  onEdit?: (project: Project) => void;
  onFile?: (project: Project) => void;
  onExpand?: () => void;
  onCollapse?: () => void;
  permissions?: Record<string, boolean>;
}

const ProjectInfoCard: React.FC<ProjectInfoCardProps> = ({
  project,
  isExpanded,
  showEdit = true,
  showFile = true,
  showExpand = true,
  showCollapse = false,
  enableEdit = true,
  enableFile = true,
  enableExpand = true,
  enableCollapse = true,

  onEdit,
  onFile,
  onExpand,
  onCollapse,
  permissions,
}) => {
  const navigate = useNavigate();

  const handleEditClick = () => {
    if (!enableEdit) return;

    // If parent wants to handle edit
    if (onEdit) {
      if (!isFullProject(project)) {
        console.warn('Edit attempted with ProjectPreview');
        return;
      }

      onEdit(project);
      return;
    }

    // Internal navigation requires full project data
    if (!isFullProject(project)) {
      console.warn('Navigation requires full Project data');
      return;
    }

    navigate('/dashboard/project-management/create', {
      state: {
        project: {
          id: project?.id,
          name: project?.name,
          maxUsers: project?.sites.length ?? 0,
          maxSites: project?.sites.length ?? 0,
          sites: project?.sites || [],
          primaryColor: project?.primaryColour,
          secondaryColor: project?.secondaryColour,
          emoji: project?.emoji,
          description: project?.descriptions,
          admins: project?.managers || [],
        },
        from: 'project-management',
      },
    });
  };

  const handleFileClick = () => {
    if (!enableFile) return;

    if (!isFullProject(project)) {
      console.warn('File action requires full Project');
      return;
    }

    onFile?.(project); // safe
  };

  const handleShrinkClick = () => {
    navigate(-1);
  };

  const isArchived = isFullProject(project) ? !project?.status : false;

  return (
    <div className="bg-[#F2EBEE] rounded-xl p-3">
      <div className="flex justify-between items-stretch">
        {/* LEFT SECTION */}
        <div className="flex items-stretch gap-4 min-w-0 flex-1">
          <div className="bg-[#AFAFAF]/50 rounded-lg flex items-center justify-center p-3 aspect-square flex-shrink-0">
            <span className="text-3xl">{parseEmoji(project?.emoji)}</span>
          </div>

          <div className="flex flex-col justify-between capitalize min-w-0 flex-1">
            <div className="text-lg font-medium">{project?.id}</div>
            <div
              className="text-2xl font-semibold overflow-hidden text-ellipsis whitespace-nowrap"
              title={project?.name}
            >
              {project?.name}
            </div>
          </div>
        </div>

        {/* RIGHT SECTION */}
        <div className="flex gap-2">
          {!isExpanded && (
            <>
              {showEdit && (
                <button
                  disabled={!enableEdit || !permissions?.edit}
                  onClick={handleEditClick}
                  className={`rounded-lg px-3 bg-[#AFAFAF33] hover:bg-[#9c9b9b33] disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <img src="/edit.png" alt="Edit" className="w-8 h-8" />
                </button>
              )}

              {showFile && (
                <button
                  disabled={!enableFile || !permissions?.edit}
                  onClick={handleFileClick}
                  className={`rounded-lg px-3 bg-[#AFAFAF33] hover:bg-[#9c9b9b33] disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <img
                    src="/Frame.png"
                    alt="File"
                    className="w-8 h-8"
                    style={{ opacity: isArchived ? 0.5 : 1 }}
                  />
                </button>
              )}
            </>
          )}

          <button
            disabled={!enableExpand}
            onClick={handleShrinkClick}
            className="rounded-lg px-3 bg-[#AFAFAF33] hover:bg-[#9c9b9b33] disabled:opacity-40"
          >
            <Shrink size={35} strokeWidth={1.5} color="#4A082D" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectInfoCard;
